import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import KYCModalScreen from '../../../KYCModalScreen';
import LegalPersonInformation from '../../substeps/LegalPersonInformation';
import LegalPersonDocuments from '../../substeps/LegalPersonDocuments';
import {bankingActions} from '../../../../../../state/actions/banking.actions';
import {useBankingCompanyState, useKYCCompanyHeaders} from '../../../../../../hooks';
import {kycHelpers, objectHelpers} from '../../../../../../utils/helpers';
import {bankingDocumentTypes, bankingUserTypesConstants} from '../../../../../../constants';

const LegalPerson = ({
  onFinish,
  ...rest
}) => {
  const dispatch = useDispatch();
  const companyState = useBankingCompanyState();
  const kycHeaders = useKYCCompanyHeaders();
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [t] = useTranslation('main');
  const [documents, setDocuments] = useState({});

  const informationFormRef = useRef(null);
  const documentsFormRef = useRef(null);
  // Slots the backend has already accepted, so a resubmit after a partial failure does not
  // re-send them and leave duplicates on the KYC.
  const acceptedDocumentsRef = useRef(new Set());

  const okDisabled = useMemo(() => step === 1 ? false : objectHelpers.isNaV(documents?.visura), [step, documents])

  useEffect(() => {
    const state = objectHelpers.getObjProp(companyState, 'state');
    if (state) {
      const states = {
        'lp_null': 1,
        'lp_creating': 2
      }
      const activeStep = states[state] || 2;
      if (activeStep !== step) setStep(activeStep);

      if (userId === null && activeStep !== 1) {
        dispatch(bankingActions.getCardsUsers({
          query: {user_type: bankingUserTypesConstants.LEGAL_PERSON},
          successCallback: (users) => {
            if (users.length > 0) setUserId(users[0].id);
          },
          headers: kycHeaders
        }))
      }
    }
  }, [companyState]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOk = () => {
    const formsRef = {
      1: informationFormRef,
      2: documentsFormRef
    }
    const formRef = formsRef[step];
    if (formRef && formRef.current) formRef.current.submit();
  }

  const handleInformationSubmit = ({userId}) => {
    dispatch(bankingActions.getCompanyState({headers: kycHeaders}));
    setStep(2);
    setUserId(userId);
  }

  const handleDocumentsSubmit = (documents, successCallback, errorCallback) => {
    const slots = [
      {name: 'visura', documentTypeId: bankingDocumentTypes.COMPANY_REGISTRATION},
      {name: 'statuto', documentTypeId: bankingDocumentTypes.BUSINESS_LEGAL_STATUS}
    ];
    const toUpload = slots
      .filter(({name}) => documents?.[name])
      .map(({name, documentTypeId}) => ({name, file: documents[name], documentTypeId}));

    if (toUpload.length === 0) {
      errorCallback && errorCallback({name: 'visura', response: t('validation.pleaseUploadDocument')});
      return;
    }

    kycHelpers.uploadDocumentsSequentially({
      documents: toUpload,
      userId,
      createDocument: (data, successCallback, errorCallback) => dispatch(bankingActions.createDocument({data, successCallback, errorCallback, headers: kycHeaders})),
      accepted: acceptedDocumentsRef.current,
      onSuccess: () => {
        successCallback && successCallback();
        onFinish && onFinish();
      },
      onError: (failure) => errorCallback && errorCallback(failure)
    });
  }

  const getStepContent = () => {
    const screens = {
      1: (
        <LegalPersonInformation
          className='left-side-content'
          onSubmit={handleInformationSubmit}
          ref={informationFormRef}
        />
      ),
      2: (
        <LegalPersonDocuments
          onChange={setDocuments}
          onSubmit={handleDocumentsSubmit}
          ref={documentsFormRef}
        />
      )
    }
    return screens[step] || null;
  }

  const stepContent = getStepContent();

  return (
    <KYCModalScreen
      enabledCancel={false}
      onOk={(props) => !okDisabled && onOk(props)}
      okProps={{disabled: okDisabled}}
      {...rest}
    >
      {stepContent}
    </KYCModalScreen>
  );
}

LegalPerson.propTypes = {
  onFinish: PropTypes.func
}

export default LegalPerson;
